import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UnifiedToolInfo } from '../types';
import { ArrowLeftIcon, ArrowRightIcon } from './Icons';

interface ToolTabsProps {
  tools: UnifiedToolInfo[];
  activeToolId: string;
  navigateTo: (path: string) => void;
}

export const ToolTabs: React.FC<ToolTabsProps> = ({ tools, activeToolId, navigateTo }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    navigateTo(path);
  };

  const checkScrollability = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      const isScrolledToStart = el.scrollLeft < 5;
      const isScrolledToEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 5;
      const hasOverflow = el.scrollWidth > el.clientWidth;

      setCanScrollLeft(hasOverflow && !isScrolledToStart);
      setCanScrollRight(hasOverflow && !isScrolledToEnd);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      checkScrollability();
      el.addEventListener('scroll', checkScrollability, { passive: true });
      
      const resizeObserver = new ResizeObserver(checkScrollability);
      resizeObserver.observe(el);
      resizeObserver.observe(document.body);

      // Auto scroll active tab into view
      const activeElement = el.querySelector('[aria-current="page"]');
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      return () => {
        el.removeEventListener('scroll', checkScrollability);
        resizeObserver.disconnect();
      };
    }
  }, [tools, activeToolId, checkScrollability]);

  const handleScroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (el) {
      const scrollAmount = el.clientWidth * 0.6;
      el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  if (tools.length <= 1) {
    return null;
  }

  return (
    <div className="relative my-4">
      {canScrollLeft && (
        <button
          onClick={() => handleScroll('left')}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none transition-all"
          aria-label="Scroll left"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
      )}

      <div 
        ref={scrollRef} 
        className="bg-slate-100/90 dark:bg-slate-900/90 p-1.5 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-x-auto hide-scrollbar flex items-center gap-1.5 shadow-xs"
      >
        {tools.map((tool) => {
          const isActive = tool.id === activeToolId;
          const Icon = tool.icon;
          return (
            <a
              key={tool.id}
              href={`/tool/${tool.id}`}
              onClick={(e) => handleNavigate(e, `/tool/${tool.id}`)}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 flex items-center gap-2 flex-shrink-0 ${
                isActive
                  ? 'bg-white dark:bg-slate-800 text-cyan-600 dark:text-cyan-400 shadow-sm border border-slate-200/60 dark:border-slate-700 scale-[1.02]'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-white/50 dark:hover:bg-slate-800/50'
              }`}
              aria-current={isActive ? 'page' : undefined}
            >
              {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
              <span>{tool.label}</span>
            </a>
          );
        })}
      </div>

      {canScrollRight && (
        <button
          onClick={() => handleScroll('right')}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none transition-all"
          aria-label="Scroll right"
        >
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
